import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, MenuItem, Select, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { getMyActivity, getAllActivity, getMyEvents } from '../../api/eventApi';

const FONT = "'Poppins', sans-serif";

const mins = (s) => `${Math.floor((s || 0) / 60)} min`;
const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleString('en-MY', { day: 'numeric', month: 'short', year: '2-digit', hour: 'numeric', minute: '2-digit' })
    : '—';

const StatCard = ({ label, value, sub, borderColor }) => (
    <Card sx={{ borderLeft: `4px solid ${borderColor}`, borderRadius: '14px', flex: 1, minWidth: 150 }}>
        <CardContent sx={{ p: '14px !important', '&:last-child': { pb: '14px !important' } }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.68rem', color: '#64748B', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '1.2rem', fontWeight: 700, color: '#0F172A', mt: 0.5 }}>{value}</Typography>
            {sub && <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: '#64748B', mt: 0.25 }}>{sub}</Typography>}
        </CardContent>
    </Card>
);

/* ============================================================================
   ACTIVITY REPORT — two pages off one component

     /reports/activity      My Activity      — a participant's own record
     /reports/activity/all  All Activity     — organisers only, route-guarded

   Same split, and the same reason, as the commission and incentive reports: a
   participant must never see what anybody else did, so the company-wide view has
   its own route and its own endpoint rather than a hidden tab on a page they can
   load. The My page calls MyActivity, which is scoped to the caller server-side,
   so even a broken route cannot hand over someone else's rows.

   The numbers come from the player, not from Zoom — Zoom has not reported attendee
   attention since 2020. "Active screen" is the share of the time watched where the
   tab was actually in front. (user 2026-10-03)
   ========================================================================= */
export default function ActivityReport({ allScope = false }) {
    const [data, setData] = useState({ summary: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [events, setEvents] = useState([]);
    const [eventId, setEventId] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = allScope
                ? await getAllActivity({ eventId: eventId || undefined })
                : await getMyActivity();
            setData(res.data || { summary: [], rows: [] });
        } catch {
            setData({ summary: [], rows: [] });
        } finally {
            setLoading(false);
        }
    }, [allScope, eventId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!allScope) return;
        getMyEvents().then(r => setEvents((r.data || []).filter(e => !e.isTeaser))).catch(() => {});
    }, [allScope]);

    const totals = useMemo(() => {
        const rows = data.rows || [];
        const attended = rows.filter(r => r.status === 'Attended' || r.watchedSeconds > 0);
        const watchedRows = rows.filter(r => r.watchedSeconds > 0);
        return {
            sessions: rows.length,
            attended: attended.length,
            watchSeconds: rows.reduce((a, r) => a + (r.watchedSeconds || 0), 0),
            avgCompletion: watchedRows.length
                ? Math.round(watchedRows.reduce((a, r) => a + (r.completionPercent || 0), 0) / watchedRows.length)
                : 0,
            people: new Set(rows.map(r => r.userId)).size,
        };
    }, [data]);

    const exportCsv = () => {
        const cfg = mkConfig({
            fieldSeparator: ',', useKeysAsHeaders: true,
            filename: allScope ? 'Event_Activity_All' : 'Event_Activity_Mine',
        });
        const rows = (data.rows || []).map(r => ({
            'Event': r.eventTitle,
            'Type': r.eventType,
            'Session': fmtDate(r.startsOn),
            'Topic': r.topic ?? '',
            'Agent': r.agentName,
            'Status': r.status,
            'Watched (min)': Math.floor((r.watchedSeconds || 0) / 60),
            'Active screen %': r.focusPercent ?? '',
            'Complete %': r.completionPercent ?? '',
            'First seen': r.firstSeenOn ? fmtDate(r.firstSeenOn) : '',
            'Last seen': r.lastSeenOn ? fmtDate(r.lastSeenOn) : '',
        }));
        download(cfg)(generateCsv(cfg)(rows.length ? rows : [{}]));
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '1.3rem', color: '#0F172A', mb: 0.5 }}>
                {allScope ? 'All Activity' : 'My Activity'}
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '0.82rem', color: '#64748B', mb: 2 }}>
                {allScope
                    ? 'Who joined what, how long they stayed, and how much of each session they actually watched.'
                    : 'Your own record — the sessions you joined and how much of each you watched.'}
            </Typography>

            <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                <StatCard label="Sessions" value={totals.sessions} borderColor="#0E8FD1" />
                <StatCard label="Attended" value={totals.attended} borderColor="#059669" />
                <StatCard label="Watch time" value={mins(totals.watchSeconds)} borderColor="#7C3AED" />
                <StatCard label="Avg complete" value={`${totals.avgCompletion}%`}
                    sub="of the sessions watched" borderColor="#B45309" />
                {allScope && <StatCard label="People" value={totals.people} borderColor="#475569" />}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                {allScope && (
                    <Select size="small" displayEmpty value={eventId} onChange={e => setEventId(e.target.value)}
                        sx={{ minWidth: 220, fontFamily: FONT, fontSize: '0.8rem' }}>
                        <MenuItem value="" sx={{ fontFamily: FONT, fontSize: '0.8rem' }}>Every event</MenuItem>
                        {events.map(e => (
                            <MenuItem key={e.eventId} value={e.eventId} sx={{ fontFamily: FONT, fontSize: '0.8rem' }}>
                                {e.title}
                            </MenuItem>
                        ))}
                    </Select>
                )}
                <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}
                    sx={{ fontFamily: FONT, textTransform: 'none' }}>
                    Export CSV
                </Button>
                <Button size="small" onClick={load} sx={{ fontFamily: FONT, textTransform: 'none' }}>Refresh</Button>
            </Stack>

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={26} /></Stack>
            ) : (
                <>
                    {allScope && (data.summary || []).length > 0 && (
                        <Card sx={{ borderRadius: '14px', mb: 2 }}>
                            <CardContent>
                                <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.92rem', mb: 1 }}>
                                    Per person
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {['Agent', 'Team', 'Invited', 'Joined', 'Attended', 'Watch time', 'Avg complete', 'Last seen'].map(h => (
                                                    <TableCell key={h} sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.summary.map(s => (
                                                <TableRow key={s.userId} hover>
                                                    <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem', fontWeight: 600 }}>{s.agentName}</TableCell>
                                                    <TableCell sx={{ fontFamily: FONT, fontSize: '0.76rem', color: '#64748B' }}>{s.groupName || '—'}</TableCell>
                                                    <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem' }}>{s.sessionsInvited}</TableCell>
                                                    <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem' }}>{s.sessionsJoined}</TableCell>
                                                    <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem', fontWeight: 700, color: '#065F46' }}>
                                                        {s.sessionsAttended}
                                                    </TableCell>
                                                    <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem' }}>{mins(s.totalWatchedSeconds)}</TableCell>
                                                    <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem' }}>{s.avgCompletionPercent}%</TableCell>
                                                    <TableCell sx={{ fontFamily: FONT, fontSize: '0.74rem', color: '#64748B' }}>{fmtDate(s.lastSeenOn)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}

                    <Card sx={{ borderRadius: '14px' }}>
                        <CardContent>
                            <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.92rem', mb: 1 }}>
                                Session by session
                            </Typography>
                            <TableContainer sx={{ maxHeight: 560 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            {[
                                                'Event', 'Session', ...(allScope ? ['Agent'] : []),
                                                'Status', 'Watched', 'Active screen', 'Complete',
                                            ].map(h => (
                                                <TableCell key={h} sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(data.rows || []).length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={allScope ? 7 : 6}
                                                    sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#94A3B8', textAlign: 'center', py: 4 }}>
                                                    Nothing recorded yet.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {(data.rows || []).map(r => (
                                            <TableRow key={`${r.eventSessionId}-${r.userId}`} hover>
                                                <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem' }}>
                                                    {r.eventTitle}
                                                    {r.topic && (
                                                        <Typography sx={{ fontFamily: FONT, fontSize: '0.68rem', color: '#94A3B8' }}>{r.topic}</Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: FONT, fontSize: '0.74rem', color: '#64748B' }}>{fmtDate(r.startsOn)}</TableCell>
                                                {allScope && (
                                                    <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem', fontWeight: 600 }}>{r.agentName}</TableCell>
                                                )}
                                                <TableCell>
                                                    <Chip label={r.status} size="small"
                                                        sx={{
                                                            height: 19, fontSize: '0.62rem', fontWeight: 700,
                                                            bgcolor: r.status === 'Attended' ? '#D1FAE5'
                                                                : r.status === 'NoShow' ? '#FEE2E2'
                                                                    : r.status === 'Requested' ? '#FEF3C7' : '#F1F5F9',
                                                            color: r.status === 'Attended' ? '#065F46'
                                                                : r.status === 'NoShow' ? '#991B1B'
                                                                    : r.status === 'Requested' ? '#92400E' : '#475569',
                                                        }} />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem' }}>{mins(r.watchedSeconds)}</TableCell>
                                                <TableCell sx={{ fontFamily: FONT, fontSize: '0.78rem' }}>
                                                    {r.watchedSeconds > 0 ? `${r.focusPercent}%` : '—'}
                                                </TableCell>
                                                <TableCell sx={{
                                                    fontFamily: FONT, fontSize: '0.78rem', fontWeight: 700,
                                                    color: r.completionPercent >= 80 ? '#065F46' : r.completionPercent >= 40 ? '#92400E' : '#64748B',
                                                }}>
                                                    {r.completionPercent ? `${r.completionPercent}%` : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </>
            )}
        </Box>
    );
}
